import { useState, useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Menu, X, Phone, LogIn, MessageSquarePlus } from 'lucide-react'
import { cn } from '../../lib/utils'
import { NAV_LINKS, CONTACT_INFO, SOCIAL_LINKS } from '../../lib/constants'
import Button from '../ui/Button'
import { useAuth } from '../../hooks/useAuth'
import TicketFormModal from '../features/Ticketing/TicketFormModal'

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
)

function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [ticketModalOpen, setTicketModalOpen] = useState(false)
  const location = useLocation()
  const { user, isAdmin } = useAuth()

  const isDashboard = location.pathname.startsWith('/dashboard')

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
  }, [mobileMenuOpen])

  // Ne pas afficher le header sur les pages dashboard
  if (isDashboard) {
    return null
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-white'
      )}
    >
      <nav className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
            aria-label="Cy Art Luthier - Accueil"
          >
            <img
              src="/images/logos/logo-cy-rectangle.webp"
              alt="Cy Art Luthier"
              className="h-16 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Mobile Quick Links (between logo and burger) */}
          <div className="flex lg:hidden items-center gap-4">
            <NavLink
              to="/portfolio"
              className={({ isActive }) =>
                cn(
                  'text-base font-medium transition-colors duration-200',
                  isActive
                    ? 'text-primary'
                    : 'text-gray-700 hover:text-primary'
                )
              }
            >
              Mes créations
            </NavLink>
            <NavLink
              to="/services"
              className={({ isActive }) =>
                cn(
                  'text-base font-medium transition-colors duration-200',
                  isActive
                    ? 'text-primary'
                    : 'text-gray-700 hover:text-primary'
                )
              }
            >
              Services
            </NavLink>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'text-xl font-medium transition-colors duration-200 relative',
                    'after:absolute after:bottom-0 after:left-0 after:h-0.5 after:bg-primary',
                    'after:transition-all after:duration-300',
                    isActive
                      ? 'text-primary after:w-full'
                      : 'text-gray-700 hover:text-primary after:w-0 hover:after:w-full'
                  )
                }
              >
                {item.name}
              </NavLink>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={CONTACT_INFO.phoneLink}
              className="flex items-center gap-2 text-xl text-primary hover:text-primary-dark transition-colors font-semibold"
            >
              <Phone className="h-5 w-5 text-primary" />
              <span className="font-semibold">{CONTACT_INFO.phone}</span>
            </a>
            <a
              href={SOCIAL_LINKS.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 bg-[#25D366] text-white rounded-lg text-sm font-medium hover:bg-[#20BA5A] transition-all duration-200 shadow-sm hover:shadow-md"
              aria-label="WhatsApp"
            >
              <WhatsAppIcon className="h-4 w-4" />
              <span>WhatsApp</span>
            </a>
            {user && isAdmin && (
              <button
                onClick={() => setTicketModalOpen(true)}
                className="flex items-center gap-2 px-3 py-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                aria-label="Créer un ticket support"
              >
                <MessageSquarePlus className="h-5 w-5" />
                <span className="text-sm font-medium hidden lg:inline">Support</span>
              </button>
            )}
            {user ? (
              <Link to="/dashboard">
                <Button size="sm">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/contact">
                  <Button size="sm">Me Contacter</Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" size="sm">
                    <LogIn className="h-4 w-4 mr-2" />
                    Connexion
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Navigation — Backdrop */}
      <div
        onClick={() => setMobileMenuOpen(false)}
        className={cn(
          'lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]',
          'transition-opacity duration-300',
          mobileMenuOpen
            ? 'opacity-100 visible'
            : 'opacity-0 invisible pointer-events-none'
        )}
        aria-hidden="true"
      />

      {/* Mobile Navigation — Drawer */}
      <div
        className={cn(
          'lg:hidden fixed top-0 right-0 bottom-0 w-1/2 max-w-[220px] z-50',
          'flex flex-col',
          'transition-transform duration-300 ease-in-out',
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        style={{ background: 'rgba(15,15,15,0.97)', backdropFilter: 'blur(20px)', borderLeft: '1px solid rgba(255,159,28,0.15)' }}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 pt-6 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <img
            src="/images/logos/logo-cy-rectangle.webp"
            alt="Cy Art Luthier"
            className="h-10 w-auto"
          />
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: 'rgba(255,255,255,0.5)' }}
            aria-label="Fermer le menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex flex-col px-3 pt-3 gap-0.5">
          {NAV_LINKS.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  'py-2.5 px-4 rounded-lg text-base font-medium transition-all duration-200',
                  isActive
                    ? 'text-primary bg-primary/10'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                )
              }
            >
              {item.name}
            </NavLink>
          ))}
        </nav>

        {/* CTAs en bas */}
        <div className="mt-auto px-4 pb-8 pt-4 space-y-2.5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <a
            href={SOCIAL_LINKS.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 py-2.5 px-4 bg-[#25D366] text-white rounded-xl text-sm font-medium hover:bg-[#20BA5A] transition-all duration-200"
            aria-label="WhatsApp"
          >
            <WhatsAppIcon className="h-4 w-4" />
            <span>WhatsApp</span>
          </a>
          <a
            href={CONTACT_INFO.phoneLink}
            className="flex items-center justify-center gap-2 py-2 text-sm text-primary font-semibold transition-colors"
          >
            <Phone className="h-4 w-4" />
            <span>{CONTACT_INFO.phone}</span>
          </a>
          {user && isAdmin && (
            <button
              onClick={() => { setTicketModalOpen(true); setMobileMenuOpen(false) }}
              className="w-full py-2.5 px-4 flex items-center justify-center gap-2 rounded-xl text-sm font-medium transition-all duration-200"
              style={{ background: 'rgba(255,159,28,0.1)', color: '#FF9F1C' }}
            >
              <MessageSquarePlus className="h-4 w-4" />
              <span>Créer un ticket</span>
            </button>
          )}
          {user ? (
            <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="block">
              <Button className="w-full" size="sm">Dashboard</Button>
            </Link>
          ) : (
            <>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block">
                <Button className="w-full" size="sm">Me Contacter</Button>
              </Link>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block">
                <Button variant="outline" size="sm" className="w-full" style={{ borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}>
                  <LogIn className="h-4 w-4 mr-2" />
                  Connexion
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Ticket Form Modal */}
      <TicketFormModal
        isOpen={ticketModalOpen}
        onClose={() => setTicketModalOpen(false)}
      />
    </header>
  )
}

export default Header
